import type { PresignUploadResponse } from "@hybrid-escrow/shared";

export interface UploadResult {
  etag?: string;
}

/**
 * Uploads a file directly to the presigned target returned by
 * `POST /listings/:id/uploads/presign` (S3-style presigned POST with `fields`, or a
 * presigned PUT with `headers`), reporting progress via XHR (fetch doesn't expose upload
 * progress). The file itself never touches our API server — it goes straight to the
 * private quarantine bucket per Build.md.
 */
export function uploadToPresignedTarget(
  target: PresignUploadResponse,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(target.method, target.url, true);

    if (target.method === "PUT") {
      for (const [key, value] of Object.entries(target.headers ?? {})) {
        xhr.setRequestHeader(key, value);
      }
    }

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve({ etag: xhr.getResponseHeader("ETag") ?? undefined });
      } else {
        reject(new Error(`Upload failed (status ${xhr.status}). The file was not accepted.`));
      }
    });
    xhr.addEventListener("error", () => reject(new Error("Upload failed — connection error.")));
    xhr.addEventListener("abort", () => reject(new Error("Upload was cancelled.")));

    if (target.method === "PUT") {
      xhr.send(file);
    } else {
      const formData = new FormData();
      for (const [key, value] of Object.entries(target.fields ?? {})) {
        formData.append(key, value);
      }
      // The file field must be appended last for S3-style presigned POST policies.
      formData.append("file", file);
      xhr.send(formData);
    }
  });
}
